import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { TransactionProvider } from './context/TransactionContext';
import { SettingsProvider } from './context/SettingsContext';
import { GoalsProvider } from './context/GoalsContext';
import { ToastProvider } from './context/ToastContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import MobileNav from './components/MobileNav';
import ToastContainer from './components/Toast';
import Dashboard from './pages/Dashboard';
import Transactions from './pages/Transactions';
import Reports from './pages/Reports';
import Goals from './pages/Goals';
import AIAssistant from './pages/AIAssistant';
import Settings from './pages/Settings';
import Login from './pages/Login';

function App() {
  const [theme, setTheme] = useState(() => {
    try {
      const savedTheme = localStorage.getItem('theme') || 'light';
      document.documentElement.setAttribute('data-theme', savedTheme);
      return savedTheme;
    } catch (error) {
      console.warn('localStorage not available:', error);
      return 'light';
    }
  });

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    try {
      localStorage.setItem('theme', newTheme);
    } catch (error) {
      console.warn('Failed to save theme:', error);
    }
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  useEffect(() => {
    const handleThemeChange = (event) => {
      setTheme(event.detail);
    };
    
    window.addEventListener('themeChange', handleThemeChange);
    return () => window.removeEventListener('themeChange', handleThemeChange);
  }, []);

  return (
    <AuthProvider>
      <SettingsProvider>
        <GoalsProvider>
          <ToastProvider>
            <TransactionProvider>
              <AppContent theme={theme} onToggleTheme={toggleTheme} />
            </TransactionProvider>
          </ToastProvider>
        </GoalsProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}

function AppContent({ theme, onToggleTheme }) {
  const { isAuthenticated, loading } = useAuth();
  const [showLogin, setShowLogin] = useState(true);

  useEffect(() => {
    setShowLogin(!isAuthenticated);
  }, [isAuthenticated]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (showLogin) {
    return (
      <>
        <Login onLogin={() => setShowLogin(false)} />
        <ToastContainer />
      </>
    );
  }

  return (
    <div className="app" data-theme={theme}>
      <Header theme={theme} onToggleTheme={onToggleTheme} />
      <div className="main-layout">
        <Sidebar />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/transactions" element={<Transactions />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/goals" element={<Goals />} />
            <Route path="/ai-assistant" element={<AIAssistant />} />
            <Route path="/settings" element={<Settings />} />
          </Routes>
        </main>
      </div>
      <MobileNav />
      <ToastContainer />
    </div>
  );
}

export default App;